import FileHelper from "@/helpers/utilities/FileHelper";

/**
 * Skobo Assignment class representing a Teams assignment
 * @category Models
 * @subcategory Education
 * @class Education
 * @name Assignment
 */

class Assignment {
  id = "";
  allowLateSubmissions = true;
  allowStudentsToAddResourcesToSubmission = true;
  assignDateTime = null;
  assignTo = {
    "@odata.type": "#microsoft.education.assignments.api.educationAssignmentClassRecipient"
  };
  assignedDateTime = "";
  closeDateTime = null;
  createdDateTime = "";
  displayName = "";
  dueDateTime = null;
  grading = {
    "@odata.type": "#microsoft.graph.educationAssignmentPointsGradeType",
    maxPoints: 0
  };
  instructions = {
    contentType: "html",
    content: ""
  };
  lastModifiedDateTime = "";
  resources = [];
  status = "draft";
  classId = "";

  // NCT specifics
  gradeType = null;
  sectionId = "";
  extSysApiSectionId = "";
  assignAssignmentOnPublish = true;

  constructor() {}

  // Getters
  /**
   * Gets Graph API payload for creating an assignment
   * @returns {object} Payload object for Graph Api from Assignment
   */
  get createPayload() {
    const createPayload = {
      displayName: this.displayName, // String
      instructions: this.instructions, // itemBody
      grading: this.grading, // educationAssignmentGradeType,
      allowLateSubmissions: this.allowLateSubmissions, // Boolean
      allowStudentsToAddResourcesToSubmission: this.allowStudentsToAddResourcesToSubmission,
      dueDateTime: this.dueDateTime, // DateTimeOffset UTC
      closeDateTime: this.closeDateTime // DateTimeOffset UTC
    };
    if (this.status === "draft") {
      createPayload.assignDateTime = this.assignDateTime; // DateTimeOffset UTC
    }

    return createPayload;
  }

  /**
   * Gets Graph API payload for patching an assignment
   * @returns {object} Payload object for Graph Api from Assignment
   */
  get patchPayload() {
    return this.createPayload;
  }

  /**
   * Expose status enum
   * @returns {object} Status enum
   */
  static get nctStatusEnum() {
    return nctStatusEnumDefinition;
  }

  /**
   * Calculates NCT status of assingment
   * @returns { string } NCT Assignment Enum status of assignment
   */
  get nctStatus() {
    if (this.status === Assignment.nctStatusEnum.DRAFT) {
      return Assignment.nctStatusEnum.DRAFT;
    }

    if (this.status === Assignment.nctStatusEnum.SCHEDULED) {
      return Assignment.nctStatusEnum.SCHEDULED;
    }

    if (this.status === Assignment.nctStatusEnum.ASSIGNED || this.status === Assignment.nctStatusEnum.PUBLISHED) {
      if (this.closeDateTime && this.closeDateTime.getTime() < new Date().getTime()) {
        return Assignment.nctStatusEnum.CLOSED;
      }

      if (this.dueDateTime.getTime() < new Date().getTime()) {
        if (this.allowLateSubmissions) {
          return Assignment.nctStatusEnum.PASSDUEDATE;
        } else {
          return Assignment.nctStatusEnum.CLOSED;
        }
      }
      return Assignment.nctStatusEnum.ASSIGNED;
    }

    return null;
  }

  //
  //
  // Methods
  validateAndRecalculateDates() {
    let compareDate = new Date();

    if (this.assignAssignmentOnPublish) {
      this.assignDateTime = null;
    } else {
      if (!this.assignDateTime) {
        const dateNow = new Date();
        dateNow.setMinutes(dateNow.getMinutes() + 5);
        this.assignDateTime = new Date();
      }
    }

    compareDate = this.assignDateTime ? new Date(this.assignDateTime) : new Date();

    if (!this.dueDateTime) {
      this.dueDateTime = new Date(compareDate);
      this.dueDateTime.setDate(compareDate.getDate() + 7);
    }

    if (this.dueDateTime < compareDate) {
      this.dueDateTime = new Date(compareDate);
    }

    if (this.allowLateSubmissions) {
      if (!this.closeDateTime) {
        this.closeDateTime = new Date(this.dueDateTime);
        this.closeDateTime.setDate(this.dueDateTime.getDate() + 2);
      }

      if (this.closeDateTime < this.dueDateTime) {
        this.closeDateTime = new Date(this.dueDateTime);
      }
    } else {
      this.closeDateTime = null;
    }
  }

  // Static Methods
  /**
   * Parse assignment object from Graph API to NCT Assignment
   * @param {object} graphAssignment    Object from graph api
   * @param {string} sectionId          NCT section id
   * @param {string} extSysApiSectionId Graph API group id
   * @returns {object} NCT assignment
   */
  static parseFromGraph(graphAssignment, sectionId, extSysApiSectionId) {
    const assignment = Object.assign(new Assignment(), graphAssignment);

    if (assignment.assignDateTime) {
      assignment.assignAssignmentOnPublish = false;
    }

    if (assignment.assignDateTime && assignment.assignDateTime !== "") {
      assignment.assignDateTime = new Date(assignment.assignDateTime);
    }

    if (assignment.assignedDateTime && assignment.assignedDateTime !== "") {
      assignment.assignedDateTime = new Date(assignment.assignedDateTime);
    }

    if (assignment.closeDateTime && assignment.closeDateTime !== "") {
      assignment.closeDateTime = new Date(assignment.closeDateTime);
    }

    if (assignment.createdDateTime && assignment.createdDateTime !== "") {
      assignment.createdDateTime = new Date(assignment.createdDateTime);
    }

    if (assignment.dueDateTime && assignment.dueDateTime !== "") {
      assignment.dueDateTime = new Date(assignment.dueDateTime);
    }

    if (assignment.lastModifiedDateTime && assignment.lastModifiedDateTime !== "") {
      assignment.lastModifiedDateTime = new Date(assignment.lastModifiedDateTime);
    }

    assignment.gradeType = null;
    assignment.sectionId = sectionId;
    assignment.extSysApiSectionId = extSysApiSectionId;

    if (assignment.resources) {
      assignment.resources.map(res => {
        res.name = res.resource.displayName;
        res.lastModifiedDateTime = res.resource.lastModifiedDateTime;
      });

      assignment.resources = FileHelper.addExtensionsToArrayOfFiles(assignment.resources);
    }

    if (assignment.submissions) {
      assignment.submissions.map(sub => {
        if (sub.submittedDateTime && sub.submittedDateTime !== "") {
          sub.submittedDateTime = new Date(sub.submittedDateTime);
        }

        if (sub.unsubmittedDateTime && sub.unsubmittedDateTime !== "") {
          sub.unsubmittedDateTime = new Date(sub.unsubmittedDateTime);
        }

        if (sub.returnedDateTime && sub.returnedDateTime !== "") {
          sub.returnedDateTime = new Date(sub.returnedDateTime);
        }

        if (sub.releasedDateTime && sub.releasedDateTime !== "") {
          sub.releasedDateTime = new Date(sub.releasedDateTime);
        }
      });
    }

    return assignment;
  }
}

const nctStatusEnumDefinition = {
  DRAFT: "draft",
  SCHEDULED: "scheduled",
  ASSIGNED: "assigned",
  PUBLISHED: "published",
  PASSDUEDATE: "passduedate",
  CLOSED: "closed" // Note this is not in MS Graph
};

export default Assignment;
